import { z } from 'zod';
import { CargoProblemMaterialSchema } from './materials.js';
import { CargoProblemMessageSchema } from './messages.js';
import { CargoProblemStatusSchema, CargoProblemTerminalOutcomeSchema } from './status.js';
import {
  CARGO_PROBLEM_COMMENT_MAX,
  CARGO_PROBLEM_DELETE_REASON_MAX,
  CARGO_PROBLEM_MESSAGE_ATTACHMENTS_MAX,
  CARGO_PROBLEM_STREAM_PAGE_DEFAULT,
  CARGO_PROBLEM_STREAM_PAGE_MAX,
} from './vocab.js';
import { CargoProblemActorSchema } from './wire.js';

/**
 * @aiPurpose one ordinary conversation message as it appears in the stream.
 * @aiInvariant the `type` literal is the discriminator that lets one strict
 *   array carry two unrelated row shapes without a client sniffing keys.
 */
export const CargoProblemWorkspaceMessageItemSchema = CargoProblemMessageSchema.extend({
  type: z.literal('MESSAGE'),
});
export type CargoProblemWorkspaceMessageItem = z.infer<
  typeof CargoProblemWorkspaceMessageItemSchema
>;

/**
 * @aiPurpose the spine every workflow card shares: which durable
 *   `CargoProblemEvent` it is, who caused it, and when.
 * @aiInvariant the consequence is NOT here — it hangs off the per-kind variant,
 *   so a kind can never carry a field whose consequence it does not have.
 */
const cargoProblemWorkspaceSystemCardBase = {
  type: z.literal('SYSTEM'),
  id: z.string().uuid(),
  actor: CargoProblemActorSchema,
  createdAt: z.string().datetime(),
};

/**
 * @aiPurpose the evidence filed with a transition note.
 * @aiInvariant the note and its files live on the card that caused them and
 *   NOWHERE else — projecting them a second time as an ordinary message is the
 *   exact duplication these shapes exist to prevent.
 */
const cargoProblemWorkspaceSystemCardAttachments = {
  attachments: z.array(CargoProblemMaterialSchema).max(CARGO_PROBLEM_MESSAGE_ATTACHMENTS_MAX),
};

/**
 * @aiPurpose the note on a transition that may be made silently — a submit, a
 *   resubmit, or a claim, whose `note` the shipped
 *   `CargoProblemTransitionInputSchema` leaves optional.
 * @aiPii participant-visible free text; it never enters the technical audit
 *   trail.
 */
const cargoProblemWorkspaceSystemCardOptionalNote = {
  note: z.string().max(CARGO_PROBLEM_COMMENT_MAX).nullable(),
  ...cargoProblemWorkspaceSystemCardAttachments,
};

/**
 * @aiPurpose the note on a transition that must be justified — reject, submit
 *   for review, evaluate.
 * @aiInvariant non-nullable AND nonblank, because the shipped
 *   `CargoProblemTransitionInputSchema` refuses those three actions without a
 *   note. A nullable slot here would let a card claim a justified move whose
 *   justification no writer could have produced.
 * @aiPii participant-visible free text; it never enters the technical audit
 *   trail.
 */
const cargoProblemWorkspaceSystemCardRequiredNote = {
  note: z
    .string()
    .max(CARGO_PROBLEM_COMMENT_MAX)
    .refine((value) => value.trim().length > 0, 'note is required'),
  ...cargoProblemWorkspaceSystemCardAttachments,
};

/**
 * @aiPurpose a workflow move between two working statuses that needs no
 *   justification: submit, resubmit, claim.
 * @aiInvariant `from`/`to` are REQUIRED and non-null: every kind in this
 *   variant moves the status, so a nullable pair would admit a card that
 *   claims a move it never made.
 */
const CargoProblemWorkspaceTransitionCardSchema = z
  .object({
    ...cargoProblemWorkspaceSystemCardBase,
    kind: z.enum(['SUBMITTED', 'RESUBMITTED', 'CLAIMED']),
    from: CargoProblemStatusSchema,
    to: CargoProblemStatusSchema,
    ...cargoProblemWorkspaceSystemCardOptionalNote,
  })
  .strict();

/**
 * @aiPurpose the two justified moves between working statuses: a reject that
 *   sends the brief back, and a review submission that hands the work on.
 * @aiInvariant identical to the silent variant except that the note is
 *   mandatory — the split exists so the card cannot be laxer than the input
 *   contract that writes it.
 */
const CargoProblemWorkspaceJustifiedTransitionCardSchema = z
  .object({
    ...cargoProblemWorkspaceSystemCardBase,
    kind: z.enum(['REJECTED', 'REVIEW_SUBMITTED']),
    from: CargoProblemStatusSchema,
    to: CargoProblemStatusSchema,
    ...cargoProblemWorkspaceSystemCardRequiredNote,
  })
  .strict();

/**
 * @aiPurpose the author's verdict that ends the problem.
 * @aiInvariant the outcome IS the destination status, so it is carried once as
 *   `to` narrowed to `CargoProblemTerminalOutcomeSchema` — a separate
 *   `outcome` key would be the same fact twice and could disagree with itself.
 * @aiInvariant the verdict's note is mandatory: `evaluate` is one of the three
 *   actions the shipped input contract refuses without one.
 */
const CargoProblemWorkspaceEvaluationCardSchema = z
  .object({
    ...cargoProblemWorkspaceSystemCardBase,
    kind: z.literal('EVALUATED'),
    from: CargoProblemStatusSchema,
    to: CargoProblemTerminalOutcomeSchema,
    ...cargoProblemWorkspaceSystemCardRequiredNote,
  })
  .strict();

/**
 * @aiPurpose the author revised the brief.
 * @aiInvariant carries no consequence beyond actor and time: the edit moves no
 *   status, and the current brief is read from the problem itself rather than
 *   copied into the stream.
 */
const CargoProblemWorkspaceEditCardSchema = z
  .object({ ...cargoProblemWorkspaceSystemCardBase, kind: z.literal('EDITED') })
  .strict();

/**
 * @aiPurpose the problem changed hands.
 * @aiInvariant `previousAssignee` is nullable — the first assignment on a
 *   problem that was reclaimed from nobody has no predecessor — while
 *   `assignee` is required, because a reassignment with no new owner is the
 *   state this flow exists to prevent.
 * @aiSecurity both sides are `{ id, name }` actors, the same display identity
 *   every timeline row already carries.
 */
const CargoProblemWorkspaceAssigneeCardSchema = z
  .object({
    ...cargoProblemWorkspaceSystemCardBase,
    kind: z.literal('ASSIGNEE_CHANGED'),
    previousAssignee: CargoProblemActorSchema.nullable(),
    assignee: CargoProblemActorSchema,
  })
  .strict();

/**
 * @aiPurpose a published attachment was removed by a moderator.
 * @aiSecurity names the row by id and states the participant-visible reason —
 *   and nothing else. The file name, MIME, and storage object key are
 *   deliberately absent: the deleted bytes must not be re-described in the
 *   conversation the deletion was meant to clear.
 */
const CargoProblemWorkspaceAttachmentDeletionCardSchema = z
  .object({
    ...cargoProblemWorkspaceSystemCardBase,
    kind: z.literal('ATTACHMENT_DELETED'),
    attachmentId: z.string().uuid(),
    reason: z.string().min(1).max(CARGO_PROBLEM_DELETE_REASON_MAX),
  })
  .strict();

/**
 * @aiPurpose legacy file activity: an attachment filed before the workspace
 *   shipped, when a file was its own event and belonged to no message.
 * @aiInvariant the consequence is one explicit `file` material, not an
 *   anonymous array. A file uploaded by the new flow arrives inside its
 *   MESSAGE or on the workflow card that carries it, and #2865's projection
 *   suppresses the duplicate event at runtime — it is not a second shape here.
 */
const CargoProblemWorkspaceLegacyFileCardSchema = z
  .object({
    ...cargoProblemWorkspaceSystemCardBase,
    kind: z.literal('FILE_UPLOADED'),
    file: CargoProblemMaterialSchema,
  })
  .strict();

/**
 * @aiPurpose one consequence-bearing workflow moment rendered inside the
 *   conversation, discriminated by the durable event `kind`.
 * @aiInvariant `COMMENT_ADDED` is absent by construction. An ordinary comment
 *   is already a `MESSAGE` row, and admitting it here would let one comment
 *   render twice in the same stream.
 */
export const CargoProblemWorkspaceSystemCardSchema = z.discriminatedUnion('kind', [
  CargoProblemWorkspaceTransitionCardSchema,
  CargoProblemWorkspaceJustifiedTransitionCardSchema,
  CargoProblemWorkspaceEvaluationCardSchema,
  CargoProblemWorkspaceEditCardSchema,
  CargoProblemWorkspaceAssigneeCardSchema,
  CargoProblemWorkspaceAttachmentDeletionCardSchema,
  CargoProblemWorkspaceLegacyFileCardSchema,
]);
export type CargoProblemWorkspaceSystemCard = z.infer<typeof CargoProblemWorkspaceSystemCardSchema>;

/**
 * @aiInvariant a plain union, because the system side is itself discriminated
 *   on `kind` and Zod's `discriminatedUnion` takes object options only. The
 *   `type` literal still separates the two families for the client; every
 *   option stays `.strict()`, so a row that matches neither is rejected rather
 *   than parsed as the other.
 */
export const CargoProblemWorkspaceStreamItemSchema = z.union([
  CargoProblemWorkspaceMessageItemSchema,
  CargoProblemWorkspaceSystemCardSchema,
]);
export type CargoProblemWorkspaceStreamItem = z.infer<typeof CargoProblemWorkspaceStreamItemSchema>;

/**
 * @aiPurpose query for the workspace stream.
 * @aiInvariant `cursor` is opaque: the server mints and parses it, so the page
 *   boundary cannot be forged into a scan of another problem. `limit` bounds a
 *   page so no client can pull an entire conversation in one roundtrip.
 */
export const CargoProblemWorkspaceMessagesQuerySchema = z
  .object({
    cursor: z.string().min(1).optional(),
    limit: z.coerce
      .number()
      .int()
      .min(1)
      .max(CARGO_PROBLEM_STREAM_PAGE_MAX)
      .default(CARGO_PROBLEM_STREAM_PAGE_DEFAULT),
  })
  .strict();
export type CargoProblemWorkspaceMessagesQuery = z.infer<
  typeof CargoProblemWorkspaceMessagesQuerySchema
>;

/**
 * @aiPurpose one page of the merged conversation stream.
 * @aiInvariant a cursor-free request answers with the NEWEST page, and the
 *   items inside a page are ordered oldest→newest so it renders top-to-bottom
 *   as it was written. A non-null `nextCursor` fetches the next OLDER page:
 *   paging walks backwards into history, and `null` means the caller has
 *   reached the beginning of the conversation.
 * @aiInvariant this cursor is therefore not a live tail. A reconnect or a
 *   window refocus picks up new rows through #2865's narrow re-read of the
 *   newest page, never by walking this cursor forward.
 */
export const CargoProblemWorkspaceMessagesResponseSchema = z
  .object({
    items: z.array(CargoProblemWorkspaceStreamItemSchema),
    nextCursor: z.string().min(1).nullable(),
  })
  .strict();
export type CargoProblemWorkspaceMessagesResponse = z.infer<
  typeof CargoProblemWorkspaceMessagesResponseSchema
>;
